/**
 * ReadMoreDescription
 * Adds a "Read More" / "Read Less" toggle to MEC event descriptions.
 * The template (list/render.php) renders a short excerpt plus the full
 * event content, and only outputs the full content when there's more to
 * show. This class injects a toggle for those and swaps between the two.
 */
class ReadMoreDescription {
  constructor({
    descriptionSelector = ".mec-event-description",
    fullSelector = ".mec-event-description__full",
    expandedClass = "mec-event-description--expanded",
    toggleClass = "mec-event-description-toggle",
    readMoreLabel = "Read More",
    readLessLabel = "Read Less",
  } = {}) {
    this.descriptionSelector = descriptionSelector;
    this.fullSelector = fullSelector;
    this.expandedClass = expandedClass;
    this.toggleClass = toggleClass;
    this.readMoreLabel = readMoreLabel;
    this.readLessLabel = readLessLabel;
  }

  /**
   * Finds every MEC event description on the page and sets up a toggle
   * button for the ones that carry expandable full content.
   */
  init() {
    const descriptions = document.querySelectorAll(this.descriptionSelector);
    descriptions.forEach((description) => this._setupDescription(description));
  }

  /**
   * Sets up a toggle button for a single description, but only when the
   * template rendered full content to reveal. The button is inserted
   * right after the description.
   * @param {HTMLElement} description
   */
  _setupDescription(description) {
    const full = description.querySelector(this.fullSelector);
    if (!full) return;

    // Content is hidden with the `hidden` attribute at render time; CSS
    // now owns visibility via the expanded class, so drop the attribute.
    full.removeAttribute("hidden");

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = `${this.toggleClass} orange-btn--txt-only`;
    toggle.textContent = this.readMoreLabel;
    toggle.setAttribute("aria-expanded", "false");
    toggle.addEventListener("click", () => this._toggle(description, toggle));

    description.insertAdjacentElement("afterend", toggle);
  }

  /**
   * Swaps a description between its excerpt and full content, syncing the
   * toggle button's label and aria-expanded state.
   * @param {HTMLElement} description
   * @param {HTMLButtonElement} toggle
   */
  _toggle(description, toggle) {
    const isExpanded = description.classList.toggle(this.expandedClass);
    toggle.setAttribute("aria-expanded", String(isExpanded));
    toggle.textContent = isExpanded ? this.readLessLabel : this.readMoreLabel;
  }
}

const initReadMoreDescription = () => {
  const readMore = new ReadMoreDescription();
  readMore.init();
};

export default initReadMoreDescription;
